import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { recordsApi } from '../lib/api';

// Records decrypted per request while filling in the skeleton. ~8 ms each
// server-side, so a batch is well under a second.
export const DECRYPT_BATCH = 30;

/**
 * Two-step record loading, shared by My Life and the organisation page:
 *  1. GET /records/index — plaintext skeleton, renders at once (blurred).
 *  2. POST /records/list {ids} — decrypt in batches, viewport first
 *     (cards report themselves via `onVisible`), the rest in the background.
 *
 * `filter(record)` narrows the skeleton (e.g. to one organisation) before
 * anything is decrypted; `search` (a string) runs a server-side search
 * inside the decrypted text and returns only matches with a snippet.
 */
export function useLazyRecords({ filter = null, search = '' } = {}) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decryptedCount, setDecryptedCount] = useState(0);
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searching, setSearching] = useState(false);
  const pendingRef = useRef(new Set());
  const priorityRef = useRef([]);
  const pumpingRef = useRef(false);
  const generationRef = useRef(0);

  const mergeDecrypted = useCallback((list) => {
    if (!list?.length) return;
    const byId = Object.fromEntries(list.map((r) => [r.id, r]));
    setRecords((prev) => prev.map((r) => byId[r.id] || r));
    setDecryptedCount((n) => n + list.length);
  }, []);

  const pump = useCallback(async () => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;
    const generation = generationRef.current;
    try {
      while (pendingRef.current.size > 0 && generation === generationRef.current) {
        const batch = [];
        while (batch.length < DECRYPT_BATCH && priorityRef.current.length) {
          const id = priorityRef.current.shift();
          if (pendingRef.current.has(id)) batch.push(id);
        }
        for (const id of pendingRef.current) {
          if (batch.length >= DECRYPT_BATCH) break;
          if (!batch.includes(id)) batch.push(id);
        }
        batch.forEach((id) => pendingRef.current.delete(id));
        try {
          const res = await recordsApi.list(null, batch);
          if (generation !== generationRef.current) return;
          mergeDecrypted(res.data?.records || []);
        } catch (e) {
          if (generation !== generationRef.current) return;
          if (e?.response?.status && ![401, 422].includes(e.response.status)) {
            console.error('Records: failed to decrypt a batch', e);
          }
        }
      }
    } finally {
      pumpingRef.current = false;
    }
  }, [mergeDecrypted]);

  const onVisible = useCallback((id) => {
    if (!pendingRef.current.has(id)) return;
    if (!priorityRef.current.includes(id)) priorityRef.current.push(id);
    pump();
  }, [pump]);

  const reload = useCallback(async () => {
    generationRef.current += 1;
    pendingRef.current = new Set();
    priorityRef.current = [];
    setDecryptedCount(0);
    setLoading(true);
    try {
      const res = await recordsApi.index();
      let skeleton = res.data?.records || [];
      if (filter) skeleton = skeleton.filter(filter);
      setRecords(skeleton);
      pendingRef.current = new Set(skeleton.map((r) => r.id));
      pump();
    } catch (e) {
      if (e?.response?.status && ![401, 422].includes(e.response.status)) {
        console.error('Records: failed to load the index', e);
      }
      setRecords([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pump]);

  useEffect(() => {
    reload();
    return () => { generationRef.current += 1; };
  }, [reload]);

  // Search inside: server decrypts the (filtered) set and returns matches.
  useEffect(() => {
    const q = (search || '').trim();
    if (q.length < 2) { setSearchResults(null); setSearching(false); return undefined; }
    let cancelled = false;
    setSearching(true);
    const ids = records.map((r) => r.id);
    const t = setTimeout(async () => {
      try {
        const res = await recordsApi.list(null, ids.length ? ids : null, q);
        if (!cancelled) setSearchResults(res.data?.records || []);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // records identity changes as batches decrypt; only the id set matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, records.length]);

  const pendingCount = useMemo(() => records.filter((r) => r.pending).length, [records]);

  return { records, loading, pendingCount, decryptedCount, onVisible, reload, searchResults, searching };
}
